import React, { useState } from "react";
import { useToast } from "../hooks/use-toast";
import { ActionMenu, ConfirmDialog } from "./crud";
import { SocialPostForm, SocialProfileForm } from "./record-forms";
import { Avatar } from "./ui-bits";
import { Twitter, Linkedin, Instagram, Check, Send, Sparkles, MessageSquare, PenLine, ExternalLink, Search, Plus, Users } from "lucide-react";

export const channels = [
  { key: "twitter", label: "Twitter", Icon: Twitter, color: "text-sky-500" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, color: "text-blue-600" },
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "text-pink-500" },
];

export const postStatusColor = {
  draft: "bg-[hsl(240,5%,93%)] text-[hsl(240,4%,45%)]",
  pending_approval: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  published: "bg-emerald-100 text-emerald-700",
};
export const postStatusLabel = { draft: "Draft", pending_approval: "Needs approval", approved: "Approved", published: "Published" };

const respStatusColor = {
  none: "bg-[hsl(240,5%,93%)] text-[hsl(240,4%,45%)]",
  draft: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  posted: "bg-emerald-100 text-emerald-700",
};
const respStatusLabel = { none: "No response", draft: "Draft reply", approved: "Approved", posted: "Posted" };

export function PostCard({ post, onChange, api, onEdit, onDelete }) {
  const { toast } = useToast();
  const ch = channels.find((c) => c.key === post.channel) || channels[0];
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(post.content);
  const act = async (fn, label) => { const p = await fn(); onChange(p); toast({ title: label }); };
  const save = async () => { const p = await api.updatePost(post.id, { content: text }); onChange(p); setEditing(false); toast({ title: "Post updated" }); };

  return (
    <div className="rounded-lg border border-[hsl(240,6%,91%)] bg-white p-2.5 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-1.5 mb-1.5">
        <ch.Icon className={`h-3.5 w-3.5 ${ch.color}`} />
        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${postStatusColor[post.status]}`}>{postStatusLabel[post.status]}</span>
        {post.ai_generated && <Sparkles className="h-3 w-3 text-[hsl(245,50%,55%)] ml-auto" />}
        {(onEdit || onDelete) && <ActionMenu onEdit={post.status !== "published" ? () => onEdit(post) : undefined} onDelete={() => onDelete(post)} />}
      </div>
      {editing ? (
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className="w-full text-[12px] leading-snug outline-none border border-[hsl(240,6%,90%)] rounded p-1.5 resize-none" />
      ) : (
        <p className="text-[12px] leading-snug text-[hsl(240,5%,25%)] whitespace-pre-wrap line-clamp-6">{post.content}</p>
      )}
      <div className="flex items-center gap-1 mt-2">
        {editing ? (
          <><button onClick={save} className="rounded brand-bg text-white px-2 py-0.5 text-[11px] font-medium">Save</button><button onClick={() => { setEditing(false); setText(post.content); }} className="text-[11px] text-[hsl(240,4%,45%)] px-1">Cancel</button></>
        ) : post.status !== "published" && (
          <>
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-[hsl(240,5%,95%)] text-[hsl(240,4%,45%)]"><PenLine className="h-3.5 w-3.5" /></button>
            {post.status !== "approved" && <button onClick={() => act(() => api.approvePost(post.id), "Approved")} title="Approve" className="p-1 rounded hover:bg-[hsl(240,5%,95%)] text-[hsl(240,4%,45%)]"><Check className="h-3.5 w-3.5" /></button>}
            <button onClick={() => act(() => api.publishPost(post.id), "Published")} className="ml-auto inline-flex items-center gap-1 rounded brand-bg text-white px-2 py-0.5 text-[11px] font-medium"><Send className="h-3 w-3" /> Publish</button>
          </>
        )}
      </div>
    </div>
  );
}

export function SocialProfilesPanel({ api }) {
  const { toast } = useToast();
  const [profiles, setProfiles] = React.useState([]);
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState("");
  const [profileModal, setProfileModal] = React.useState(null);
  const [postModal, setPostModal] = React.useState(null);
  const [deleting, setDeleting] = React.useState(null);
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  const [tab, setTab] = useState("profiles");
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState("all");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loadError, setLoadError] = useState(false);

  React.useEffect(() => {
    Promise.all([api.socialProfiles(), api.socialPosts()]).then(([pr, po]) => { setProfiles(pr); setPosts(po); }).catch(() => setLoadError(true)).finally(() => setLoading(false));
  }, [api]);

  const onPost = (p) => setPosts((prev) => prev.map((x) => (x.id === p.id ? p : x)));
  const saveProfile = async (values) => {
    const profile = profileModal.mode === "create" ? await api.addSocialProfile(values) : await api.updateSocialProfile(profileModal.row.id, values);
    setProfiles((prev) => profileModal.mode === "create" ? [profile, ...prev] : prev.map((item) => item.id === profile.id ? profile : item));
    setProfileModal(null);
    toast({ title: `Social profile ${profileModal.mode === "create" ? "added" : "updated"}` });
  };
  const savePost = async (values) => {
    const post = postModal.mode === "create" ? await api.addSocialPost(values) : await api.updateSocialPost(postModal.row.id, values);
    setPosts((prev) => postModal.mode === "create" ? [post, ...prev] : prev.map((item) => item.id === post.id ? post : item));
    setPostModal(null);
    toast({ title: `Tracked post ${postModal.mode === "create" ? "added" : "updated"}` });
  };
  const remove = async () => {
    setDeleteBusy(true);
    try {
      if (deleting.kind === "profile") {
        await api.removeSocialProfile(deleting.item.id);
        setProfiles((prev) => prev.filter((profile) => profile.id !== deleting.item.id));
      } else {
        await api.deleteSocialPost(deleting.item.id);
        setPosts((prev) => prev.filter((post) => post.id !== deleting.item.id));
      }
      toast({ title: `${deleting.kind === "profile" ? "Profile" : "Tracked post"} deleted` });
      setDeleting(null);
    } catch (error) {
      toast({ title: "Could not delete item", description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };
  const saveResp = async (post) => {
    const p = await api.updateSocialPost(post.id, { our_response: draft, response_status: draft ? "draft" : "none" });
    onPost(p); setEditing(null); toast({ title: "Response saved" });
  };

  if (loading) return <div className="p-8 text-[13px] text-[hsl(240,4%,55%)]">Loading…</div>;
  if (loadError) return <div role="alert" className="p-8 text-[13px]">Could not load social profiles. <button onClick={() => window.location.reload()} className="underline">Try again</button></div>;

  const matchesProfile = (post, profile) => post.platform === profile.platform && post.profile_handle?.replace(/^@/, "").toLowerCase() === profile.handle?.replace(/^@/, "").toLowerCase();
  const filtered = profiles.filter((p) => (platform === "all" || p.platform === platform) && [p.name, p.handle, p.bio].some((value) => (value || "").toLowerCase().includes(query.trim().toLowerCase())));
  const shownPosts = selectedProfile ? posts.filter((post) => matchesProfile(post, selectedProfile)) : posts;
  const cellClass = "px-3 py-[7px] text-[13px] border-r border-[hsl(240,6%,94%)] last:border-r-0 align-middle";

  return (
    <div className="h-full flex flex-col animate-in-up">
      <div className="flex items-center gap-1 px-4 pt-2.5 border-b border-[hsl(240,6%,92%)]">
        {[["profiles", "All profiles", Users], ["posts", "Tracked posts & responses", MessageSquare]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => { setTab(key); setSelectedProfile(null); }} aria-pressed={tab === key} className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === key ? "border-[hsl(245,58%,60%)] text-[hsl(240,6%,10%)]" : "border-transparent text-[hsl(240,4%,50%)] hover:text-[hsl(240,6%,10%)]"}`}><Icon className="h-4 w-4" />{label}</button>
        ))}
      </div>
      {tab === "profiles" ? <>
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-[hsl(240,6%,92%)]">
          <div className="rounded-md bg-[hsl(240,5%,96%)] px-2 py-[5px] text-[13px] font-medium">All {filtered.length}</div>
          <select aria-label="Filter by platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className="rounded-md bg-white px-2 py-[5px] text-[13px] text-[hsl(240,4%,45%)] border border-[hsl(240,6%,90%)]">
            <option value="all">All platforms</option>{channels.map((channel) => <option key={channel.key} value={channel.key}>{channel.label}</option>)}
          </select>
          <button onClick={() => setProfileModal({ mode: "create", row: {} })} className="inline-flex items-center gap-1.5 rounded-md brand-bg text-white px-2.5 py-[6px] text-[13px] font-medium hover:brightness-105"><Plus className="h-4 w-4" />New profile</button>
          <div className="ml-auto flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,90%)] px-2 py-[5px] w-[200px]">
            <Search className="h-[15px] w-[15px] shrink-0 text-[hsl(240,4%,55%)]" />
            <input aria-label="Search social profiles" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search profiles…" className="w-full bg-transparent text-[13px] outline-none placeholder:text-[hsl(240,4%,60%)]" />
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table aria-label="Social profiles" className="w-full border-collapse">
            <thead className="sticky top-0 bg-[hsl(240,20%,99%)] z-10"><tr className="border-b border-[hsl(240,6%,92%)]">
              {["Name", "Handle", "Platform", "Followers", "Bio", "Tracked posts", "Actions"].map((label) => <th key={label} scope="col" className="text-left px-3 py-2 text-[12px] font-medium text-[hsl(240,4%,48%)] border-r border-[hsl(240,6%,93%)] last:border-r-0 whitespace-nowrap">{label}</th>)}
            </tr></thead>
            <tbody>{filtered.map((p) => {
              const channel = channels.find((c) => c.key === p.platform);
              return <tr key={p.id} className="border-b border-[hsl(240,6%,94%)] hover:bg-[hsl(240,20%,99%)]">
                <td className={cellClass}><button onClick={() => setProfileModal({ mode: "edit", row: p })} className="flex items-center gap-2 text-left font-medium whitespace-nowrap hover:underline"><Avatar text={(p.name || p.handle || "?").slice(0, 2).toUpperCase()} brand />{p.name || p.handle}</button></td>
                <td className={cellClass}><span className="text-[hsl(245,50%,50%)] whitespace-nowrap">{p.handle}</span></td>
                <td className={cellClass}><span className="inline-flex items-center gap-1.5">{channel && <channel.Icon className={`h-4 w-4 ${channel.color}`} />}{channel?.label || p.platform}</span></td>
                <td className={cellClass}><span className="tabular-nums whitespace-nowrap">{p.followers || "—"}</span></td>
                <td className={cellClass}><p className="min-w-[180px] max-w-[360px] truncate text-[hsl(240,4%,45%)]" title={p.bio}>{p.bio || "—"}</p></td>
                <td className={cellClass}><button onClick={() => { setSelectedProfile(p); setTab("posts"); }} aria-label={`View tracked posts for ${p.handle}`} className="text-[hsl(245,50%,50%)] hover:underline tabular-nums">{posts.filter((post) => matchesProfile(post, p)).length}</button></td>
                <td className={cellClass}><ActionMenu onEdit={() => setProfileModal({ mode: "edit", row: p })} onDelete={() => setDeleting({ kind: "profile", item: p })} /></td>
              </tr>;
            })}</tbody>
          </table>
          {filtered.length === 0 && <div className="text-center py-16 text-[13px] text-[hsl(240,4%,55%)]">{profiles.length ? "No profiles match your search or platform filter." : "No social profiles yet. Add your first profile to start tracking."}</div>}
        </div>
      </> : <div className="flex-1 overflow-auto"><div className="p-4 max-w-[1000px] mx-auto">
      {/* Tracked posts + responses */}
      <div className="text-[13px] font-medium text-[hsl(240,4%,45%)] mb-2 flex items-center gap-1.5"><MessageSquare className="h-4 w-4" /> Tracked posts & our responses</div>
      {selectedProfile && <div className="mb-3 flex items-center gap-2 text-[13px]">{selectedProfile.handle}<button onClick={() => setSelectedProfile(null)} className="text-[hsl(245,50%,50%)] hover:underline">Show all posts</button></div>}
      <button onClick={() => setPostModal({ mode: "create", row: selectedProfile ? { profile_handle: selectedProfile.handle, profile_name: selectedProfile.name, platform: selectedProfile.platform } : {} })} className="mb-3 rounded-md border border-[hsl(240,6%,88%)] px-2.5 py-1 text-[12px] font-medium hover:bg-[hsl(240,5%,96%)]">Track a post</button>
      <div className="space-y-3">
        {shownPosts.length === 0 && <div className="py-12 text-center text-[13px] text-[hsl(240,4%,55%)]">No tracked posts yet.</div>}
        {shownPosts.map((post) => (
          <div key={post.id} className="rounded-xl border border-[hsl(240,6%,91%)] bg-white p-4">
            <div className="flex items-center gap-2 mb-1.5">
              {channels.filter((channel) => channel.key === post.platform).map((channel) => <channel.Icon key={channel.key} className={`h-4 w-4 ${channel.color}`} />)}
              <span className="text-[13px] font-semibold">{post.profile_name}</span>
              <span className="text-[12px] text-[hsl(240,4%,55%)]">{post.profile_handle} · {post.posted_at}</span>
              <ActionMenu onEdit={() => setPostModal({ mode: "edit", row: post })} onDelete={() => setDeleting({ kind: "post", item: post })} />
              {post.url && <a href={post.url} target="_blank" rel="noreferrer" className="ml-auto text-[hsl(245,50%,50%)]"><ExternalLink className="h-3.5 w-3.5" /></a>}
            </div>
            <p className="text-[13px] text-[hsl(240,5%,25%)]">{post.content}</p>

            <div className="mt-3 rounded-lg bg-[hsl(240,20%,99%)] border border-[hsl(240,6%,93%)] p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[hsl(245,50%,48%)]" />
                <span className="text-[12px] font-medium text-[hsl(240,4%,45%)]">Our response</span>
                <span className={`ml-auto inline-flex rounded px-1.5 py-0.5 text-[10.5px] font-medium ${respStatusColor[post.response_status] || respStatusColor.none}`}>{respStatusLabel[post.response_status] || "No response"}</span>
              </div>
              {editing === post.id ? (
                <>
                  <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} className="w-full text-[13px] outline-none border border-[hsl(240,6%,90%)] rounded p-2 resize-none" />
                  <div className="flex gap-2 mt-2"><button onClick={() => saveResp(post)} className="rounded brand-bg text-white px-2.5 py-1 text-[12px] font-medium">Save</button><button onClick={() => setEditing(null)} className="text-[12px] text-[hsl(240,4%,45%)]">Cancel</button></div>
                </>
              ) : (
                <>
                  <p className="text-[13px] text-[hsl(240,5%,30%)] whitespace-pre-wrap">{post.our_response || <span className="text-[hsl(240,4%,60%)] italic">No response drafted yet.</span>}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => { setEditing(post.id); setDraft(post.our_response || ""); }} className="inline-flex items-center gap-1 text-[12px] font-medium text-[hsl(240,4%,45%)] hover:text-[hsl(240,6%,10%)]"><PenLine className="h-3.5 w-3.5" /> Edit</button>
                    {post.our_response && post.response_status !== "posted" && (
                      <>
                        {post.response_status !== "approved" && <button onClick={async () => { const p = await api.approveSocialResponse(post.id); onPost(p); toast({ title: "Response approved" }); }} className="inline-flex items-center gap-1 rounded-md border border-[hsl(240,6%,88%)] px-2 py-0.5 text-[12px] font-medium hover:bg-[hsl(240,5%,96%)]"><Check className="h-3.5 w-3.5" /> Approve</button>}
                        <button onClick={async () => { const p = await api.markSocialPosted(post.id); onPost(p); toast({ title: "Marked as posted" }); }} className="ml-auto inline-flex items-center gap-1 rounded-md brand-bg text-white px-2 py-0.5 text-[12px] font-medium"><Send className="h-3.5 w-3.5" /> Mark posted</button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      </div></div>}
      <SocialProfileForm open={!!profileModal} onClose={() => setProfileModal(null)} onSubmit={saveProfile} initialValues={profileModal?.row} />
      <SocialPostForm open={!!postModal} onClose={() => setPostModal(null)} onSubmit={savePost} initialValues={postModal?.row} />
      <ConfirmDialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} onConfirm={remove} busy={deleteBusy} title={`Delete ${deleting?.kind === "profile" ? "profile" : "tracked post"}?`} description="This item will be permanently removed." />
    </div>
  );
}
